import React, { useEffect, useState } from 'react';
import {
  Activity, Database, HardDrive, Shield, Mail, Phone, MessageCircle,
  Settings, CheckCircle2, XCircle, ShoppingBag, Users, Package, TrendingUp,
  RefreshCw, Loader2, Inbox, AlertTriangle, Eye, MousePointerClick, Ban,
} from 'lucide-react';
import { AdminAPI, formatPrice } from '../../lib/api';
import { Button } from '../ui/button';

const INTEG_LABELS = {
  resend: { label: 'Resend (Email)', icon: Mail },
  twilio_sms: { label: 'Twilio SMS', icon: Phone },
  twilio_whatsapp: { label: 'Twilio WhatsApp', icon: MessageCircle },
  general: { label: 'Paramètres généraux', icon: Settings },
};

const STAT_CARD = (label, value, sub, icon, accent = '#1F4D3F') => ({ label, value, sub, icon, accent });

const Pill = ({ ok, labelOk = 'Opérationnel', labelKo = 'Hors service' }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {ok ? labelOk : labelKo}
  </span>
);

const Tile = ({ icon: Icon, label, value, sub, accent = '#1F4D3F', testid }) => (
  <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-[#7A6F5C] uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}
    </div>
    <div className="font-serif text-2xl font-semibold mt-1.5" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-xs text-[#7A6F5C] mt-1">{sub}</div>}
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const EVENT_META = {
  'email.delivered': { label: 'Livré', icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50' },
  'email.opened': { label: 'Ouvert', icon: Eye, cls: 'text-blue-700 bg-blue-50' },
  'email.clicked': { label: 'Cliqué', icon: MousePointerClick, cls: 'text-[#1F4D3F] bg-[#E4EDE9]' },
  'email.bounced': { label: 'Rebond', icon: AlertTriangle, cls: 'text-amber-700 bg-amber-50' },
  'email.complained': { label: 'Plainte', icon: Ban, cls: 'text-rose-700 bg-rose-50' },
  'email.sent': { label: 'Envoyé', icon: Inbox, cls: 'text-[#7A6F5C] bg-[#F5F1EA]' },
};

const SystemStatusTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try { setData(await AdminAPI.systemStatus()); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return <div className="text-center py-10 text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>;
  }
  if (!data) return null;

  const orderRate = data.orders_30d.total;
  const conversionPct = data.counts.users && orderRate ? Math.round((orderRate / data.counts.users) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="admin-system-status-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#7A6F5C]">Mise à jour automatique toutes les 30s · dernière vérification {formatDate(data.now)}</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={refreshing} className="border-[#E8E0D0]" data-testid="system-status-refresh">
          {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Rafraîchir
        </Button>
      </div>

      {/* Health row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs text-[#7A6F5C] uppercase tracking-wider flex items-center gap-1"><Database className="w-3.5 h-3.5" /> Base de données</div>
            <Pill ok={data.db.ok} />
          </div>
          <div className="font-mono text-[11px] text-[#7A6F5C]">{data.db.name} · {data.db.latency_ms}ms</div>
        </div>
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs text-[#7A6F5C] uppercase tracking-wider flex items-center gap-1"><HardDrive className="w-3.5 h-3.5" /> Object Storage</div>
            <Pill ok={data.storage.ok} labelOk="Initialisé" labelKo="Non init." />
          </div>
          <div className="text-[11px] text-[#7A6F5C]">Uploads images CMS</div>
        </div>
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs text-[#7A6F5C] uppercase tracking-wider flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Rate limiter</div>
            <Pill ok={data.rate_limiter.ok} labelOk="Actif" />
          </div>
          <div className="text-[11px] text-[#7A6F5C]">{data.rate_limiter.backend}</div>
        </div>
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs text-[#7A6F5C] uppercase tracking-wider flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Fournisseurs en attente</div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${data.pending_suppliers > 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
              {data.pending_suppliers > 0 ? `${data.pending_suppliers} à valider` : 'À jour'}
            </span>
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
        <h3 className="font-medium text-[#2D2A26] mb-3">Intégrations tierces</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(INTEG_LABELS).map(([key, meta]) => {
            const Icon = meta.icon;
            const ok = data.integrations_status[key];
            return (
              <div key={key} className="flex items-center gap-3 p-3 bg-[#FAF7F2] rounded-xl border border-[#E8E0D0]" data-testid={`system-integration-${key}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${ok ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2D2A26] truncate">{meta.label}</div>
                  <Pill ok={ok} labelOk="Configuré" labelKo="Manquant" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Tile icon={Users} label="Utilisateurs" value={data.counts.users ?? '—'} testid="stat-users" />
        <Tile icon={Package} label="Produits" value={data.counts.products ?? '—'} testid="stat-products" />
        <Tile icon={Users} label="Fournisseurs" value={data.counts.suppliers ?? '—'} testid="stat-suppliers" />
        <Tile icon={ShoppingBag} label="Commandes totales" value={data.counts.orders ?? '—'} testid="stat-orders" />
        <Tile icon={TrendingUp} label="Revenu 30j" value={formatPrice(data.revenue_30d || 0)} testid="stat-revenue" accent="#C75B27" />
        <Tile icon={Activity} label="Taux conversion" value={`${conversionPct}%`} sub={`${orderRate} commandes / 30j`} testid="stat-conversion" />
      </div>

      {/* Recent orders + email events grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Last orders */}
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <h3 className="font-medium text-[#2D2A26] mb-3 flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Dernières commandes</h3>
          {data.last_orders.length === 0 ? (
            <div className="text-sm text-[#7A6F5C] text-center py-6">Aucune commande récente</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.last_orders.map(o => (
                <li key={o.id} className="flex items-center justify-between border-b border-[#F5F1EA] pb-2 last:border-0">
                  <div>
                    <div className="font-mono text-xs text-[#2D2A26]">{o.id}</div>
                    <div className="text-[10px] text-[#7A6F5C]">{formatDate(o.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-[#1F4D3F]">{formatPrice(o.total || 0)}</div>
                    <div className="text-[10px] text-[#7A6F5C] uppercase">{o.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Email analytics */}
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5">
          <h3 className="font-medium text-[#2D2A26] mb-3 flex items-center gap-2"><Mail className="w-4 h-4" /> Email analytics</h3>
          {data.email_stats.total === 0 ? (
            <div className="text-sm text-[#7A6F5C] text-center py-6">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Aucun événement reçu pour l'instant.
              <div className="text-[10px] mt-1.5">Configurez le webhook Resend dans le dashboard : <code className="px-1 bg-[#F5F1EA] rounded">/api/webhooks/resend</code></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-2 mb-3 text-center">
                {['delivered', 'opened', 'clicked', 'bounced', 'complained'].map(k => (
                  <div key={k} className="bg-[#FAF7F2] rounded-lg p-2" data-testid={`email-stat-${k}`}>
                    <div className="font-serif text-lg text-[#1F4D3F] font-semibold">{data.email_stats[k]}</div>
                    <div className="text-[9px] uppercase tracking-wider text-[#7A6F5C]">{k}</div>
                  </div>
                ))}
              </div>
              <ul className="space-y-1 text-xs max-h-48 overflow-y-auto">
                {data.last_email_events.map(ev => {
                  const meta = EVENT_META[ev.event_type] || EVENT_META['email.sent'];
                  const Icon = meta.icon;
                  return (
                    <li key={ev.id} className={`flex items-center gap-2 px-2 py-1 rounded ${meta.cls}`}>
                      <Icon className="w-3 h-3" />
                      <span className="font-medium">{meta.label}</span>
                      <span className="truncate flex-1">{(ev.to || [])[0] || '—'}</span>
                      <span className="text-[10px] opacity-70">{formatDate(ev.received_at)}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemStatusTab;
